import lodash from 'lodash';
import { DateTime, Settings } from 'luxon';
import { isNotNull, isNull } from './dataUtils';

const { isDate, isString } = lodash;
Settings.defaultZone = 'America/Sao_Paulo';
Settings.defaultLocale = 'pt-BR';

const dt = DateTime;

export function createDateTime(date: string | Date): DateTime | null {
  const { fromFormat, fromJSDate, fromISO } = dt;
  let dateTime = null;
  if (!date) return dateTime;

  if (isDate(date) && fromJSDate(date).isValid) {
    dateTime = fromJSDate(date).toUTC();
  }

  if (isString(date) && fromFormat(date, 'dd/MM/yyyy').isValid) {
    dateTime = fromFormat(date, 'dd/MM/yyyy').toUTC();
  }

  if (isString(date) && fromISO(date).isValid) {
    dateTime = fromISO(date).toUTC();
  }

  return dateTime;
}

export function isValidDate(date: string | Date) {
  const { fromFormat, fromJSDate, fromISO } = dt;
  let result = false;

  if (isDate(date)) {
    result = fromJSDate(date).isValid;
  }

  if (isString(date)) {
    result = fromFormat(date, 'dd/MM/yyyy').isValid || fromISO(date).isValid;
  }

  return result;
}

export function formatToBrazilianDate(date: string | Date) {
  try {
    const dateTime = createDateTime(date);

    return isNotNull(dateTime) ? dateTime.toLocaleString() : '';
  } catch (err) {
    console.error('ERROR:FORMAT_TO_BR_DATE:', err);
    return '';
  }
}

export function formatToISODate(date: Date | string | null | undefined): string {
  try {
    if (!date) return '';

    const dateTime = createDateTime(date);
    return isNotNull(dateTime) ? (dateTime.toISODate() ?? '') : '';
  } catch (err) {
    console.error('ERROR:FORMAT_TO_ISO_DATE:', err);
    return '';
  }
}

export function isToday(date: string | Date): boolean {
  try {
    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) return false;

    const today = new Date();

    return (
      parsedDate.getDate() === today.getDate() &&
      parsedDate.getMonth() === today.getMonth() &&
      parsedDate.getFullYear() === today.getFullYear()
    );
  } catch (err) {
    console.error('ERROR:IS_TODAY:', err);
    return false;
  }
}

export function isDateGraterThan(date: string | Date, compareDate: string | Date) {
  try {
    if (!isValidDate(date) || !isValidDate(compareDate)) return false;

    const dateTime = createDateTime(date);
    const compareDateTime = createDateTime(compareDate);

    if (isNull(dateTime) || isNull(compareDateTime)) {
      throw new Error('Invalid date');
    }

    return dateTime > compareDateTime;
  } catch (err) {
    console.error('ERROR:IS_DATE_GRATER_THAN:', err);
  }
}

export function isDateMinorThan(date: string | Date, compareDate: string | Date) {
  try {
    if (!isValidDate(date) || !isValidDate(compareDate)) return false;

    const dateTime = createDateTime(date);
    const compareDateTime = createDateTime(compareDate);

    if (isNull(dateTime) || isNull(compareDateTime)) {
      throw new Error('Invalid date');
    }

    return dateTime < compareDateTime;
  } catch (err) {
    console.error('ERROR:IS_DATE_MINOR_THAN:', err);
  }
}

export function isSameDate(date: string | Date, compareDate: string | Date) {
  try {
    if (!isValidDate(date) || !isValidDate(compareDate)) return false;

    const dateTime = createDateTime(date);
    const compareDateTime = createDateTime(compareDate);

    if (isNull(dateTime) || isNull(compareDateTime)) {
      throw new Error('Invalid date');
    }

    return dateTime.toISODate() === compareDateTime.toISODate();
  } catch (err) {
    console.error('ERROR:IS_SAME_DATE:', err);
  }
}

export function getActualYear() {
  return dt.now().year;
}

export function adaptDateParams(date: Date[]) {
  const dateStart = DateTime.fromJSDate(date[0]);
  const dateEnd = date[1] ? DateTime.fromJSDate(date[1]) : dateStart;

  return {
    StartDate: dateStart.toISODate(),
    EndDate: dateEnd.toISODate(),
  };
}
